import React from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { Badge } from 'src/components/ui/badge';
import type { VersionInfo } from 'src/api/rebates/types';

interface VersionSelectorProps {
  versions: VersionInfo[];
  currentVersionNumber: number;
  onVersionChange: (versionNumber: number) => void;
  className?: string;
}

export function VersionSelector({
  versions,
  currentVersionNumber,
  onVersionChange,
  className,
}: VersionSelectorProps) {
  const sortedVersions = [...versions].sort(
    (a, b) => b.versionNumber - a.versionNumber,
  );

  const latestVersion = sortedVersions[0];
  const currentVersion = versions.find(
    (v) => v.versionNumber === currentVersionNumber,
  );

  return (
    <div className={className}>
      <Select
        value={String(currentVersionNumber)}
        onValueChange={(value) => onVersionChange(Number(value))}
      >
        <SelectTrigger className="w-[280px]">
          <div className="flex items-center gap-2">
            <span className="font-medium">Version {currentVersionNumber}</span>
            {currentVersion && (
              <Badge
                variant={
                  currentVersion.status === 'draft' ? 'default' : 'secondary'
                }
              >
                {currentVersion.status}
              </Badge>
            )}
            {currentVersionNumber === latestVersion?.versionNumber && (
              <Badge variant="outline">Latest</Badge>
            )}
          </div>
        </SelectTrigger>
        <SelectContent>
          {sortedVersions.map((version) => (
            <SelectItem key={version.id} value={String(version.versionNumber)}>
              <div className="flex items-center gap-2 py-1">
                <span className="font-medium">
                  Version {version.versionNumber}
                </span>
                <Badge
                  variant={version.status === 'draft' ? 'default' : 'secondary'}
                  className="text-xs"
                >
                  {version.status}
                </Badge>
                {version.versionNumber === latestVersion?.versionNumber && (
                  <Badge variant="outline" className="text-xs">
                    Latest
                  </Badge>
                )}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Created {new Date(version.createdAt).toLocaleDateString()}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
